import { ElementRef, Injectable, OnDestroy, inject } from '@angular/core';

import { BehaviorSubject, from, fromEvent, Observable, skip, Subject, Subscription, switchMap, take } from 'rxjs';

import { ConfigService, mapErrorForMonitoring, MonitoringLogger, WindowRef } from '@fcom/core';
import { finShare } from '@fcom/rx';

import {
  AgentforceChatConfig,
  CustomAgentforceEventRoles,
  EmbeddedBootstrapSvc,
  HelpButtonStatus,
  LOCALE_TO_AGENTFORCE_LANG,
  AgentforceHiddenProfileFields,
  AgentforceChatFields,
  SupportedAgentforceLanguages,
} from '../interfaces';
import { SalesforceChatService } from './salesforce-chat.service';

@Injectable()
export class AgentforceChatService implements OnDestroy {
  private sfChatService = inject(SalesforceChatService);
  private windowRef = inject(WindowRef);
  private configService = inject(ConfigService);
  private monitoringLogger = inject(MonitoringLogger);

  private subscription = new Subscription();
  renderAudioElement$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  chatButtonState$: BehaviorSubject<HelpButtonStatus> = new BehaviorSubject(HelpButtonStatus.VISIBLE);
  disableChat$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  messagingForWebTriggered$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  openWebMessagingChat$: Subject<AgentforceChatFields & AgentforceHiddenProfileFields> = new Subject<
    AgentforceChatFields & AgentforceHiddenProfileFields
  >();

  chatLanguage: SupportedAgentforceLanguages;
  embeddedSvcBootstrap: EmbeddedBootstrapSvc;
  agentforceConfig?: AgentforceChatConfig;

  constructor() {
    this.agentforceConfig = this.configService.cfg.agentforceChat;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private determineLanguage(langValue: string): SupportedAgentforceLanguages {
    return LOCALE_TO_AGENTFORCE_LANG[langValue] || LOCALE_TO_AGENTFORCE_LANG['_DEFAULT'];
  }

  setAgentforceLanguage(langValue: string): void {
    const language: SupportedAgentforceLanguages = this.determineLanguage(langValue); // currently only supporting en and fi (da, ee, etc sites also use en language chat.)

    // Check when chat needs to reload, chat can't differentiate between languages when switching for eg: fi-en -> fi-fi
    if (this.chatLanguage === language) {
      return;
    }

    if (
      !(this.agentforceConfig && this.agentforceConfig.disableLangRefresh) &&
      this.chatLanguage !== undefined &&
      this.chatLanguage !== language
    ) {
      return this.windowRef.nativeWindow.location.reload();
    }

    this.chatLanguage = language;
  }

  webMessagingChat(formData: AgentforceChatFields, target: ElementRef): void {
    this.sfChatService
      .load(this.agentforceConfig.chatScriptSrcURL)
      .then((): void => {
        this.embeddedSvcBootstrap = this.windowRef.nativeWindow['embeddedservice_bootstrap'];
      })
      .catch((error: unknown): void => {
        this.monitoringLogger.error('Error loading Messaging for Web chat:', { error: mapErrorForMonitoring(error) });
      })
      .finally((): void => {
        if (!this.embeddedSvcBootstrap) {
          return;
        }

        const messageReady$: Observable<Event> = fromEvent(
          this.windowRef.nativeWindow,
          'onEmbeddedMessagingReady'
        ).pipe(finShare());

        // set the user data sent to salesforce
        this.subscription.add(
          messageReady$
            .pipe(take(1))
            .subscribe((): void => this.embeddedSvcBootstrap.prechatAPI.setHiddenPrechatFields(formData))
        );

        // close chat
        this.subscription.add(
          messageReady$
            .pipe(
              skip(1),
              switchMap((): Observable<void> => from(this.embeddedSvcBootstrap.userVerificationAPI.clearSession(true)))
            )
            .subscribe((): void => this.chatButtonState$.next(HelpButtonStatus.VISIBLE))
        );

        // chat has finished initializing, creates button in the background, this is when we simulate the user click to auto-launch the chat for the user.
        this.subscription.add(
          fromEvent(this.windowRef.nativeWindow, 'onEmbeddedMessagingButtonCreated')
            .pipe(
              take(1), // prevent auto re-launch on close
              switchMap((): Observable<void> => from(this.embeddedSvcBootstrap.utilAPI.launchChat()))
            )
            .subscribe((): void => {
              this.chatButtonState$.next(HelpButtonStatus.HIDDEN);
              this.messagingForWebTriggered$.next(true);
            })
        );

        // maximise chat
        this.subscription.add(
          fromEvent(this.windowRef.nativeWindow, 'onEmbeddedMessagingWindowMaximized').subscribe((): void =>
            this.chatButtonState$.next(HelpButtonStatus.HIDDEN)
          )
        );

        // minimise chat, added to re-inforce hide the button action in instances where it persists.
        this.subscription.add(
          fromEvent(this.windowRef.nativeWindow, 'onEmbeddedMessagingWindowMinimized').subscribe((): void =>
            this.chatButtonState$.next(HelpButtonStatus.HIDDEN)
          )
        );

        // notification sound
        this.subscription.add(
          fromEvent(this.windowRef.nativeWindow, 'onEmbeddedMessageSent').subscribe((event: CustomEvent): void => {
            const audioElement: HTMLAudioElement = this.windowRef.nativeWindow.document.getElementById(
              'agentforce-chat-notification'
            ) as HTMLAudioElement;

            if (audioElement && event.detail?.conversationEntry?.sender?.role !== CustomAgentforceEventRoles.END_USER) {
              audioElement.play().catch((error: unknown): void => {
                this.monitoringLogger.error('Error playing chat notification sound:', {
                  error: mapErrorForMonitoring(error),
                });
              });
            }
          })
        );

        this.embeddedSvcBootstrap.settings.shouldShowParticipantChgEvntInConvHist = true; // SHA disable transfer alerts
        this.embeddedSvcBootstrap.settings.devMode = false;
        this.embeddedSvcBootstrap.settings.language = this.chatLanguage;
        this.embeddedSvcBootstrap.settings.chatButtonPosition = '0.4rem, 0.4rem';
        this.embeddedSvcBootstrap.settings.hideChatButtonOnLoad = true;
        this.embeddedSvcBootstrap.settings.targetElement = target.nativeElement;

        this.embeddedSvcBootstrap.init(
          this.agentforceConfig.queueConfigs.deploymentId,
          this.agentforceConfig.queueConfigs.buttonId,
          this.agentforceConfig.queueConfigs.agentUrl,
          {
            scrt2URL: this.agentforceConfig.scrt2URL,
          }
        );
      });
  }

  startWebMessagingChat(formData: AgentforceChatFields): void {
    if (this.embeddedSvcBootstrap) {
      if (this.agentforceConfig && this.agentforceConfig.disableLangRefresh) {
        this.embeddedSvcBootstrap.settings.language = this.chatLanguage;
      }
      this.embeddedSvcBootstrap.prechatAPI.setHiddenPrechatFields(formData);
      // Allows chat to launch on re-submission after user has ended the conversation
      this.chatButtonState$.next(HelpButtonStatus.CHAT);
      this.embeddedSvcBootstrap.utilAPI.launchChat();
    }
  }
}
